import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { commentRepliesQuery } from '@/entities/project-comment';
import { useCommentsScope } from '../../model/comments.store';
import type { CommentData } from './CommentThread.types';

/**
 * Annotation-agnostic replies slice shared by the annotated (asset) and plain
 * (brief) threads. Owns the lazily-enabled replies query and the
 * chronologically-sorted reply list. The thread opens with the card's
 * `selected` state — `open` is passed in rather than toggled internally.
 *
 * Calls ONLY annotation-agnostic hooks (useCommentsScope, useQuery), so the
 * plain thread keeps calling zero annotation/media hooks and the Rules of
 * Hooks stay satisfied. The reply target is read from `useCommentsScope`.
 */
export function useThreadReplies(comment: CommentData, isRoot: boolean, open: boolean) {
  const { projectId, targetType, targetId } = useCommentsScope();

  const { data: repliesData, isLoading: isLoadingReplies } = useQuery({
    ...commentRepliesQuery(projectId, { targetType, targetId }, comment.id),
    enabled: isRoot && open && comment.replyCount > 0,
  });

  const replies = useMemo(() => {
    if (!isRoot) return [] as CommentData[];
    const items = (repliesData?.items ?? []) as CommentData[];
    return [...items].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [isRoot, repliesData?.items]);

  return { replies, isLoadingReplies };
}
